import React from 'react';
import {
    isRouteErrorResponse,
    useLocation,
    useNavigate,
    useRouteError
} from 'react-router-dom';
import mascot from '../../assets/images/fail_mascot.svg';
import Button from '../../components/atoms/Button';
import {useI18n} from "../../i18n";

const chunkErrorPattern = /(ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module)/i;

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
    if (isRouteErrorResponse(error)) {
        return error.statusText || error.data || fallbackMessage;
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return fallbackMessage;
};

const RouteErrorBoundary = () => {
    const {t} = useI18n();
    const error = useRouteError();
    const navigate = useNavigate();
    const location = useLocation();
    const message = getErrorMessage(error, t('error.loadPageFailed'));
    const isChunkLoadError = chunkErrorPattern.test(message);

    const handleRetry = () => {
        if (isChunkLoadError) {
            window.location.reload();
            return;
        }

        navigate(0);
    };

    return (
        <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
            <img src={mascot} alt="error" className="mb-6 w-40 max-w-[40vw]"/>
            <h1 className="mb-2 text-2xl font-bold text-BLACK dark:text-WHITE">
                {isChunkLoadError ? t('error.pageUpdateDetected') : t('error.somethingWentWrong')}
            </h1>
            <p className="mb-2 max-w-xl text-sm text-gray-500 dark:text-gray-300">
                {isChunkLoadError
                    ? t('error.pageRefreshHint')
                    : t('error.unexpectedRenderError')}
            </p>
            <p className="mb-8 max-w-xl break-words text-xs text-gray-400 dark:text-gray-500">
                {message}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
                <Button
                    title={isChunkLoadError ? t('error.refreshApp') : t('error.tryAgain')}
                    onClick={handleRetry}
                    size="short"
                    variant="primary"
                    className="short"
                />
                <Button
                    title={t('error.goHome')}
                    onClick={() => navigate('/home', {replace: location.pathname === '/home'})}
                    size="short"
                    variant="secondary"
                    className="short"
                />
            </div>
        </div>
    );
};

export default RouteErrorBoundary;
